import { XmlToolbox } from '../xmlToolbox.class';
import { SelectionPos } from './xmlCursor.class';
import { DomDummyNodeManager } from '../dummyNodes/domDummyNodeManager.class';
import { Xml2html } from '../xml-html-converter/xml2html.class';

export class XmlCursorPos {
  public ActualNode: Node; // this XMLNode actual has the editor focus
  public PosAtNode: XMLCursorPositions; // At this position the cursor is - inside oder outside the actual focussed node
  public PosInTextnode: number; // The cursor positio inside floating text when pos  is CursorInnerhalbDesTextNodes

  static getXmlCursorPositionDebugName(position: XMLCursorPositions): string {
    switch (position) {
      case XMLCursorPositions.CursorOnCompleteNode:
        return 'CursorOnCompleteNode';
      case XMLCursorPositions.CursorBehindNode:
        return 'CursorBehindNode';
      case XMLCursorPositions.CursorInEmptyNode:
        return 'CursorInEmptyNode';
      case XMLCursorPositions.CursorInsideTextNode:
        return 'CursorInsideTextNode';
      case XMLCursorPositions.CursorInFrontOfNode:
        return 'CursorInFrontOfNode';
      default:
        return `unknown cursorPos ${position}`;
    }
  }

  static getSelectionForPos(pos: XmlCursorPos): SelectionPos {
    if (DomDummyNodeManager.IsDummyNode(pos.ActualNode)) {
      throw new Error(`XmlCursorPos.getSelectionForPos: pos.AktNode ${pos.getDebugDescription()} is a dummy node!`);
    }

    if (DomDummyNodeManager.IsDummyNode(pos.ActualNode.parentElement)) {
      throw new Error(`XmlCursorPos.getSelectionForPos: pos.AktNode.parentElement ${pos.getDebugDescription()} is a dummy node!`);
    }

    switch (pos.PosAtNode) {
      case XMLCursorPositions.CursorOnCompleteNode: // (0)
        let firstChild = pos.ActualNode.firstChild;
        if (DomDummyNodeManager.IsBeforeFirstTagDummyNode(firstChild)) {
          return {
            node: firstChild.firstChild, // text inside BeforeFirstTagDummyNod
            offset: 0
          };
        } else {
          return {
            node: pos.ActualNode,
            offset: 0
          };
        }

      case XMLCursorPositions.CursorInFrontOfNode: // (1)
        if (pos.ActualNode.nodeType === Node.TEXT_NODE) {
          // text node
          const dummyBefore = pos.ActualNode.previousSibling;
          if (DomDummyNodeManager.IsDummyNode(dummyBefore)) {
            return {
              node: dummyBefore.firstChild, // before text node
              offset: 1
            };
          } else {
            throw new Error('XMLCursorPositions.CursorInFrontOfNode (1):text node: is no dummy node:  ' + pos.getDebugDescription());
          }
        } else {
          // no text node
          firstChild = pos.ActualNode.firstChild;
          if (DomDummyNodeManager.IsBeforeFirstTagDummyNode(firstChild)) {
            return {
              node: firstChild.firstChild, // text inside BeforeFirstTagDummyNod
              offset: 1
            };
          }
          if (Xml2html.isNoClosingElement(pos.ActualNode) && DomDummyNodeManager.IsDummyNode(firstChild)) {
            return { node: firstChild.firstChild, offset: 1 };
          }
          throw new Error('XMLCursorPositions.CursorInFrontOfNode (1):no text node: dont know what to do! ');
        }
        break;

      case XMLCursorPositions.CursorBehindNode: // (7, 11)
        const dummyAfter = pos.ActualNode.nextSibling;
        if (DomDummyNodeManager.IsDummyNode(dummyAfter)) {
          return {
            node: dummyAfter.firstChild, // before text node
            offset: 0
          };
        } else {
          throw new Error(`XMLCursorPositionen.CursorBehindNode should only be set, when the element has no next sibling.`);
        }

      case XMLCursorPositions.CursorInEmptyNode:
        const lastChild = pos.ActualNode.lastChild;
        if (!DomDummyNodeManager.IsDummyNode(firstChild) && !DomDummyNodeManager.IsBeforeFirstTagDummyNode(firstChild)) {
          return {
            node: lastChild.firstChild, // text inside DummyNode
            offset: 0
          };
        }
        break;

      case XMLCursorPositions.CursorInsideTextNode:
        return {
          node: pos.ActualNode,
          offset: pos.PosInTextnode
        };
    }
    throw new Error(`showCursor.getSelectionForPos unknown CursorPos ${pos.getDebugDescription()}`);
  }

  constructor() {
    this.ActualNode = null; // no node selected
    this.PosAtNode = XMLCursorPositions.CursorOnCompleteNode;
    this.PosInTextnode = 0;
  }

  clone(): XmlCursorPos {
    const klon = new XmlCursorPos();
    klon.SetCursor(this.ActualNode, this.PosAtNode, this.PosInTextnode);
    return klon;
  }

  equals(otherPos: XmlCursorPos): boolean {
    if (!otherPos) {
      return false;
    }
    if (otherPos.ActualNode !== this.ActualNode) {
      return false;
    }
    if (otherPos.PosAtNode !== this.PosAtNode) {
      return false;
    }
    if (this.PosAtNode === XMLCursorPositions.CursorInsideTextNode && this.PosInTextnode !== otherPos.PosInTextnode) {
      return false;
    }
    return true;
  }

  getDebugDescription(): string {
    return `node:${XmlToolbox.GetNodeDebugContext(this.ActualNode)}, pos:${XmlCursorPos.getXmlCursorPositionDebugName(this.PosAtNode)}, posInText:${this.PosInTextnode}`;
  }

  SetCursor(actualNode: Node, posAtNode: XMLCursorPositions, posInTextnode: number = 0) {
    if (!actualNode) {
      throw new Error('actualNode is null');
    }

    let changed: boolean;
    if (actualNode !== this.ActualNode) {
      changed = true;
    } else {
      if (posAtNode !== this.PosAtNode) {
        changed = true;
      } else {
        if (posInTextnode !== this.PosInTextnode) {
          changed = true;
        } else {
          changed = false;
        }
      }
    }

    this.ActualNode = actualNode;
    this.PosAtNode = posAtNode;
    this.PosInTextnode = posInTextnode;

    // console.log(`SetCursor: ${this.getDebugDescription()}`);

    if (changed) {
      // PosChanged(EventArgs.Empty); // inform you that the cursor has now been changed
    }
  }

  setCursorBehindNodeForDefaultOrRightMovement(node: Node) {
    const nextSibling = DomDummyNodeManager.getNextSibling(node);
    if (nextSibling) {
      this.SetCursor(nextSibling, XMLCursorPositions.CursorInFrontOfNode);
    } else {
      this.SetCursor(node, XMLCursorPositions.CursorBehindNode);
    }
  }

  setCursorpBehindNodeForLeftMovement(node: Node) {
    if (XmlToolbox.IsTextOrCommentNode(node)) {
      //  For a text node, the cursor is placed after the last character.
      this.SetCursor(node, XMLCursorPositions.CursorInsideTextNode, Math.max(0, node.textContent.length - 1));
    } else {
      const childNodeCount = DomDummyNodeManager.getChildNodeCount(node);
      if (childNodeCount === 0) {
        // There are no children in the node.
        if (!Xml2html.isNoClosingElement(node)) {
          // If the cursor shows a close tag, then put it in the empty node
          this.SetCursor(node, XMLCursorPositions.CursorInEmptyNode);
        } else {
          // If the cursor does not display a close tag, then place it before the empty node.
          this.SetCursor(node, XMLCursorPositions.CursorInFrontOfNode);
        }
      } else {
        // There are children in node
        this.SetCursor(DomDummyNodeManager.getLastChild(node), XMLCursorPositions.CursorBehindNode);
      }
    }
  }
}

export enum XMLCursorPositions {
  CursorInFrontOfNode = 0,
  CursorOnCompleteNode = 1,
  CursorInEmptyNode = 2,
  CursorInsideTextNode = 3,
  CursorBehindNode = 4
}
